import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import QrScanner from "../../components/QrScanner";
import {
  fetchDeliveryProjects,
  fetchProjectBoards,
  fetchProjectScans,
  saveProjectScan,
  closeProjectSession,
  fetchProjectDocumentFolders,
  fetchProjectOverrides,
  saveProjectOverride,
  removeProjectOverride,
  fetchProjectAuditLog,
  fetchProjectSummary,
  fetchProjectClaims,
  createProjectClaim,
} from "../../services/projectDelivery";
import { getCurrentLocation } from "../../utils/getCurrentLocation";

const norm = (s) => String(s || "").trim().toLowerCase();

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
    yellow:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
  };

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold text-black dark:text-white">
        {value}
      </div>
    </div>
  );
}

function formatAuditAction(actionType) {
  const map = {
    scan_created: "Skenirano",
    manual_entry_created: "Ručni unos",
    override_set_scanned: "Ručno označeno skenirano",
    override_set_not_scanned: "Ručno označeno nije skenirano",
    override_removed: "Uklonjen override",
    session_closed: "Zatvorena sesija",
  };

  return map[actionType] || actionType || "-";
}

function getRowTone(row) {
  switch (row?.status) {
    case "received":
      return "green";
    case "duplicate":
      return "yellow";
    case "manual_scanned":
      return "blue";
    case "manual_not_scanned":
    case "missing":
    default:
      return "red";
  }
}

function getRowClassName(row) {
  switch (row?.status) {
    case "received":
      return "border-b border-green-100 dark:border-green-950/40 bg-green-50/60 dark:bg-green-950/10 hover:bg-green-50 dark:hover:bg-green-950/20";
    case "duplicate":
      return "border-b border-yellow-100 dark:border-yellow-950/40 bg-yellow-50/60 dark:bg-yellow-950/10 hover:bg-yellow-50 dark:hover:bg-yellow-950/20";
    case "manual_scanned":
      return "border-b border-blue-100 dark:border-blue-950/40 bg-blue-50/60 dark:bg-blue-950/10 hover:bg-blue-50 dark:hover:bg-blue-950/20";
    case "manual_not_scanned":
    case "missing":
    default:
      return "border-b border-red-100 dark:border-red-950/40 bg-red-50/60 dark:bg-red-950/10 hover:bg-red-50 dark:hover:bg-red-950/20";
  }
}

export default function ProjectDeliveryCheckPage() {
  const { projectKey: routeProjectKey } = useParams();
  const location = useLocation();

const initialDocumentFolderKey = useMemo(() => {
  const params = new URLSearchParams(location.search);
  return params.get("documentFolderKey") || "";
}, [location.search]);

  const [projects, setProjects] = useState([]);
  const [projectKey, setProjectKey] = useState("");
  const [boards, setBoards] = useState([]);
  const [scans, setScans] = useState([]);
  const [manualCode, setManualCode] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [documentFolders, setDocumentFolders] = useState([]);
  const [selectedDocumentFolderKey, setSelectedDocumentFolderKey] = useState("");
  const [scanMessage, setScanMessage] = useState("");
  const [overrides, setOverrides] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryUnknownScans, setSummaryUnknownScans] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [materialFilter, setMaterialFilter] = useState("");
  const [machineFilter, setMachineFilter] = useState("");
  const [showSetup, setShowSetup] = useState(false);

  const [claims, setClaims] = useState([]);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimBoard, setClaimBoard] = useState(null);
  const [claimIssueType, setClaimIssueType] = useState("");
  const [claimDescription, setClaimDescription] = useState("");
  const [claimPhotos, setClaimPhotos] = useState([]);

  const scanMessageTimeoutRef = useRef(null);

const claimMap = useMemo(() => {
  const map = {};
  claims.forEach((c) => {
    map[norm(c.board_code)] = c;
  });
  return map;
}, [claims]);

  useEffect(() => {
    loadProjects();

    return () => {
      if (scanMessageTimeoutRef.current) {
        clearTimeout(scanMessageTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!routeProjectKey) return;
    setProjectKey(routeProjectKey);
  }, [routeProjectKey]);

  useEffect(() => {
  if (!routeProjectKey) return;
  if (!projects.length) return;

  const exists = projects.some((p) => p.projectKey === routeProjectKey);
  if (exists) {
    loadDocumentFolders(routeProjectKey);
  }
}, [routeProjectKey, projects, initialDocumentFolderKey]);

  const showScanMessage = (message) => {
    setScanMessage(message);

    if (scanMessageTimeoutRef.current) {
      clearTimeout(scanMessageTimeoutRef.current);
    }

    scanMessageTimeoutRef.current = setTimeout(() => {
      setScanMessage("");
    }, 2200);
  };

  const overrideMap = useMemo(() => {
    const map = {};
    overrides.forEach((o) => {
      map[norm(o.board_code)] = o;
    });
    return map;
  }, [overrides]);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const data = await fetchDeliveryProjects();
      setProjects(data || []);
    } catch (err) {
      console.error("loadProjects error:", err);
      alert("Greška pri učitavanju projekata.");
    } finally {
      setLoadingProjects(false);
    }
  };

  const resetProjectData = () => {
    setBoards([]);
    setScans([]);
    setOverrides([]);
    setAuditLog([]);
    setSummaryData(null);
    setSummaryRows([]);
    setSummaryUnknownScans([]);
    setClaims([]);
  };

  const loadBoards = async (selectedProjectKey, documentFolderKey) => {
    try {
      setLoadingBoards(true);

      const [
  boardsData,
  scansData,
  overridesData,
  auditData,
  summaryRes,
  claimsData
] = await Promise.all([
  fetchProjectBoards(selectedProjectKey, documentFolderKey),
  fetchProjectScans(selectedProjectKey),
  fetchProjectOverrides(selectedProjectKey),
  fetchProjectAuditLog(selectedProjectKey, 100),
  fetchProjectSummary(selectedProjectKey, documentFolderKey),
  fetchProjectClaims(selectedProjectKey), // ✅ sada ima svoju varijablu
]);

      setBoards(boardsData || []);
      setScans(scansData?.items || []);
      setOverrides(overridesData || []);
      setAuditLog(auditData || []);
      setSummaryData(summaryRes?.summary || null);
      setSummaryRows(summaryRes?.rows || []);
      setSummaryUnknownScans(summaryRes?.unknownScans || []);
      setClaims(claimsData || []);
    } catch (err) {
      console.error("loadBoards error:", err);
      alert("Greška pri učitavanju stavki projekta.");
    } finally {
      setLoadingBoards(false);
    }
  };

  const loadDocumentFolders = async (selectedProjectKey) => {
  try {
    const folders = await fetchProjectDocumentFolders(selectedProjectKey);
    setDocumentFolders(folders || []);

    const preferredFolderKey =
      folders.find((f) => f.key === initialDocumentFolderKey)?.key || "";

    const defaultFolderKey = preferredFolderKey || folders?.[0]?.key || "";

    setSelectedDocumentFolderKey(defaultFolderKey);

    if (defaultFolderKey) {
      await loadBoards(selectedProjectKey, defaultFolderKey);
    } else {
      resetProjectData();
    }
  } catch (err) {
    console.error("loadDocumentFolders error:", err);
    alert("Greška pri učitavanju Documents foldera.");
    setDocumentFolders([]);
    setSelectedDocumentFolderKey("");
    resetProjectData();
  }
};

  const isCodeExpectedForProject = async (code) => {
    return boards.some((b) => norm(b.board_code) === norm(code));
  };

  const registerScan = async (code, source = "scanner", meta = {}) => {
    const cleanCode = String(code || "").trim();
    if (!cleanCode || !projectKey) return;

    const location = await getCurrentLocation();
    const exists = boards.some((b) => norm(b.board_code) === norm(cleanCode));

    try {
      const saved = await saveProjectScan(projectKey, {
        board_code: cleanCode,
        source,
        matched: exists,
        raw_text: meta.rawText || cleanCode,
        scan_format: meta.format || null,
        note: meta.note || null,
        latitude: location?.latitude || null,
        longitude: location?.longitude || null,
        location_accuracy: location?.accuracy || null,
        location_label: null,
      });

      if (saved) {
        setScans((prev) => [saved, ...prev]);

        if (selectedDocumentFolderKey) {
          await loadBoards(projectKey, selectedDocumentFolderKey);
        }

        showScanMessage(
          exists
            ? `✅ Kod pronađen i upisan: ${cleanCode}`
            : `⚠️ Kod nije pronađen u izabranom Documents folderu: ${cleanCode}`
        );
      } else {
        showScanMessage("⚠️ Skenirano, ali odgovor servera nije vraćen.");
      }
    } catch (err) {
      console.error("registerScan error:", err);
      alert("Greška pri upisu skena.");
    }
  };

  const handleManualAdd = async () => {
    if (!manualCode.trim()) return;
    await registerScan(manualCode, "manual");
    setManualCode("");
  };

  const applyOverrideLocalState = (boardCode, item) => {
    setOverrides((prev) => {
      const rest = prev.filter((x) => norm(x.board_code) !== norm(boardCode));
      return item ? [item, ...rest] : rest;
    });
  };

  const rows = useMemo(() => {
    return boards.map((board) => {
      const key = norm(board.board_code);

      const qtyScannedReal = scans.filter(
        (s) => norm(s.board_code) === key && Number(s.matched) === 1
      ).length;

      const override = overrideMap[key] || null;

      let qtyScanned = qtyScannedReal;
      let status = "missing";
      let status_label = "Nedostaje";

      if (override?.forced_status === "scanned") {
        qtyScanned = 1;
        status = "manual_scanned";
        status_label = "Ručno označeno: primljeno";
      } else if (override?.forced_status === "not_scanned") {
        qtyScanned = 0;
        status = "manual_not_scanned";
        status_label = "Ručno označeno: nije primljeno";
      } else if (qtyScannedReal === 1) {
        status = "received";
        status_label = "Primljeno";
      } else if (qtyScannedReal > 1) {
        status = "duplicate";
        status_label = "Duplikat";
      }

      return {
        ...board,
        override,
        qty_expected: 1,
        qty_scanned: qtyScanned,
        qty_scanned_real: qtyScannedReal,
        missing: Math.max(1 - qtyScanned, 0),
        status,
        status_label,
      };
    });
  }, [boards, scans, overrideMap]);

  const unknownScans = useMemo(() => {
    return scans.filter((s) => Number(s.matched) !== 1);
  }, [scans]);

  const summary = useMemo(() => {
    const totalExpected = boards.length;
    const totalMatchedUnique = rows.filter((r) => r.qty_scanned >= 1).length;
    const totalMissing = rows.filter((r) => r.qty_scanned === 0).length;
    const totalDuplicates = rows.filter((r) => r.qty_scanned_real > 1).length;

    return {
      totalExpected,
      totalMatchedUnique,
      totalMissing,
      totalDuplicates,
      totalUnknown: unknownScans.length,
    };
  }, [boards, rows, unknownScans]);

  const displayRows = useMemo(
    () => (summaryRows.length ? summaryRows : rows),
    [summaryRows, rows]
  );

  const displayUnknownScans = useMemo(
    () => (summaryUnknownScans.length ? summaryUnknownScans : unknownScans),
    [summaryUnknownScans, unknownScans]
  );

  const displaySummary = useMemo(() => {
    if (summaryData) {
      return {
        totalExpected: summaryData.expectedCount || 0,
        totalMatchedUnique: summaryData.receivedCount || 0,
        totalMissing: summaryData.missingCount || 0,
        totalDuplicates: summaryData.duplicateCount || 0,
        totalUnknown: summaryData.unknownCount || 0,
      };
    }

    return summary;
  }, [summaryData, summary]);

  const materialOptions = useMemo(() => {
    return Array.from(
      new Set(
        displayRows
          .map((r) => r.material_model || r.material_name)
          .filter(Boolean)
      )
    ).sort();
  }, [displayRows]);

  const machineOptions = useMemo(() => {
    return Array.from(
      new Set(displayRows.map((r) => r.machine_target).filter(Boolean))
    ).sort();
  }, [displayRows]);

  const filteredRows = useMemo(() => {
    return displayRows.filter((row) => {
      const q = search.trim().toLowerCase();

      const matchesSearch =
        !q ||
        String(row.board_code || "").toLowerCase().includes(q) ||
        String(row.file_name || "").toLowerCase().includes(q) ||
        String(row.material_model || "").toLowerCase().includes(q) ||
        String(row.material_name || "").toLowerCase().includes(q) ||
        String(row.comment_1 || "").toLowerCase().includes(q) ||
        String(row.comment_2 || "").toLowerCase().includes(q) ||
        String(row.machine_target || "").toLowerCase().includes(q) ||
        String(row.board_id || "").toLowerCase().includes(q) ||
        String(row.barcode || "").toLowerCase().includes(q);

      const matchesStatus =
        !statusFilter || String(row.status || "") === statusFilter;

      const matchesMaterial =
        !materialFilter ||
        row.material_model === materialFilter ||
        row.material_name === materialFilter;

      const matchesMachine =
        !machineFilter || row.machine_target === machineFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesMaterial &&
        matchesMachine
      );
    });
  }, [displayRows, search, statusFilter, materialFilter, machineFilter]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
  <div className="flex flex-col gap-4">
    <div>
      <h1 className="text-2xl font-bold text-black dark:text-white">
        QA/QC — Kontrola kvaliteta i kvantiteta
      </h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Provera očekivanih i evidentiranih stavki kroz skeniranje, upoređivanje i ručnu kontrolu odstupanja.
      </p>
    </div>

    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setShowScanner(true)}
        disabled={!projectKey}
        className="w-full px-4 py-3 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        📷 Skeniraj
      </button>

      <div>
        <label className="block text-sm mb-1 text-black dark:text-white">
          Ručni unos koda
        </label>
        <input
          type="text"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          placeholder="Ručni unos koda"
          className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
        />
      </div>

      <button
        type="button"
        onClick={handleManualAdd}
        disabled={!projectKey}
        className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 disabled:opacity-50"
      >
        Unesi ručno
      </button>

      <button
        type="button"
        onClick={() => setShowSetup((prev) => !prev)}
        className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800"
      >
        {showSetup ? "Sakrij podešavanja prijema" : "Prikaži podešavanja prijema"}
      </button>
    </div>
  </div>

  {showSetup && (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5">
          <label className="block text-sm mb-1 text-black dark:text-white">
            Projekat
          </label>
          <select
            value={projectKey}
            onChange={async (e) => {
              const value = e.target.value;
              setProjectKey(value);

              if (value) {
                await loadDocumentFolders(value);
              } else {
                setDocumentFolders([]);
                setSelectedDocumentFolderKey("");
                resetProjectData();
              }
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">-- Izaberi projekat --</option>
            {projects.map((p) => (
              <option key={p.projectKey} value={p.projectKey}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-4">
          <label className="block text-sm mb-1 text-black dark:text-white">
            Documents folder
          </label>
          <select
            value={selectedDocumentFolderKey}
            onChange={async (e) => {
              const value = e.target.value;
              setSelectedDocumentFolderKey(value);

              if (projectKey && value) {
                await loadBoards(projectKey, value);
              } else {
                resetProjectData();
              }
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            disabled={!projectKey}
          >
            <option value="">-- Izaberi Documents folder --</option>
            {documentFolders.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="lg:col-span-3 flex items-end">
          <button
            type="button"
            onClick={async () => {
              if (!projectKey) return;
              try {
                await closeProjectSession(projectKey);
                showScanMessage("✅ Sesija prijema je zatvorena.");
                if (projectKey && selectedDocumentFolderKey) {
                  await loadBoards(projectKey, selectedDocumentFolderKey);
                }
              } catch (err) {
                console.error(err);
                alert("Greška pri zatvaranju sesije.");
              }
            }}
            disabled={!projectKey}
            className="w-full px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-black dark:text-white hover:bg-gray-50 dark:hover:bg-neutral-800 disabled:opacity-50"
          >
            Zatvori prijem
          </button>
        </div>
      </div>

      {(loadingProjects || loadingBoards) && (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {loadingProjects && <div>Učitavanje projekata...</div>}
          {loadingBoards && <div>Učitavanje stavki projekta...</div>}
        </div>
      )}
    </div>
  )}
</div>
      

          


      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard label="Očekivano" value={displaySummary.totalExpected} />
        <StatCard label="Primljeno" value={displaySummary.totalMatchedUnique} />
        <StatCard label="Nedostaje" value={displaySummary.totalMissing} />
        <StatCard label="Duplikati" value={displaySummary.totalDuplicates} />
        <StatCard label="Nepoznato" value={displaySummary.totalUnknown} />
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Očekivane stavke
          </h2>
        </div>

        {scanMessage && (
          <div className="mx-4 mt-4 rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-3 text-sm font-medium text-black dark:text-white">
            {scanMessage}
          </div>
        )}

        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pretraga: kod, fajl, materijal, komentar, barcode..."
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">-- Svi statusi --</option>
            <option value="received">Primljeno</option>
            <option value="missing">Nedostaje</option>
            <option value="duplicate">Duplikat</option>
            <option value="manual_scanned">Ručno primljeno</option>
            <option value="manual_not_scanned">Ručno nije primljeno</option>
          </select>

          <select
            value={materialFilter}
            onChange={(e) => setMaterialFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">-- Svi materijali --</option>
            {materialOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>

          <select
            value={machineFilter}
            onChange={(e) => setMachineFilter(e.target.value)}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          >
            <option value="">-- Sve mašine --</option>
            {machineOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1800px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Kod
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Fajl
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Dimenzije
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Materijal
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Kant trake
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Komentari
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Mašina
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Skenirano
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Nedostaje
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Status
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Akcije
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.board_code}
                  className={getRowClassName(row)}
                >
                  <td className="p-3 text-black dark:text-white font-semibold">
  <div className="flex items-center gap-2">
    <span>{row.board_code}</span>
    {claimMap[norm(row.board_code)] && (
      <Badge tone="yellow">Reklamacija</Badge>
    )}
  </div>
</td>

                  <td className="p-3 text-black dark:text-white">
                    {row.file_name}
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    {row.width && row.height ? `${row.width} × ${row.height}` : "-"}
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    <div className="space-y-1">
                      <div className="font-medium">{row.material_model || "-"}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {row.material_name || "-"}
                      </div>
                    </div>
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    <div className="space-y-1 text-xs">
                      <div>L: {row.edge_left || "-"}</div>
                      <div>R: {row.edge_right || "-"}</div>
                      <div>T: {row.edge_top || "-"}</div>
                      <div>B: {row.edge_bottom || "-"}</div>
                    </div>
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    <div className="space-y-1 text-xs">
                      <div>{row.comment_1 || "-"}</div>
                      {row.comment_2 && row.comment_2 !== row.comment_1 && (
                        <div className="text-gray-500 dark:text-gray-400">
                          {row.comment_2}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    <div className="space-y-1">
                      <div>{row.machine_target || "-"}</div>
                      {row.barcode && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          BC: {row.barcode}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    {row.qty_scanned}
                  </td>

                  <td className="p-3 text-black dark:text-white">
                    {row.missing}
                  </td>

                  <td className="p-3">
                    <Badge tone={getRowTone(row)}>
                      {row.status_label || row.status}
                    </Badge>
                  </td>

                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const item = await saveProjectOverride(projectKey, {
                              board_code: row.board_code,
                              forced_status: "scanned",
                              note: "Manual override: scanned",
                            });

                            applyOverrideLocalState(row.board_code, item);
                            if (selectedDocumentFolderKey) {
                              await loadBoards(projectKey, selectedDocumentFolderKey);
                            }
                            showScanMessage(`✅ Ručno označeno kao skenirano: ${row.board_code}`);
                          } catch (err) {
                            console.error(err);
                            alert("Greška pri ručnom upisu statusa.");
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900"
                      >
                        Označi skenirano
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const item = await saveProjectOverride(projectKey, {
                              board_code: row.board_code,
                              forced_status: "not_scanned",
                              note: "Manual override: not scanned",
                            });

                            applyOverrideLocalState(row.board_code, item);
                            if (selectedDocumentFolderKey) {
                              await loadBoards(projectKey, selectedDocumentFolderKey);
                            }
                            showScanMessage(`⚠️ Ručno označeno kao nije skenirano: ${row.board_code}`);
                          } catch (err) {
                            console.error(err);
                            alert("Greška pri ručnom upisu statusa.");
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                      >
                        Označi nije skenirano
                      </button>

                      <button
  type="button"
  onClick={() => {
    setClaimBoard(row);
    setClaimIssueType("");
    setClaimDescription("");
    setClaimPhotos([]);
    setShowClaimModal(true);
  }}
  className="px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 bg-orange-50 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900"
>
  Reklamacija
</button>

                      {row.override && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await removeProjectOverride(projectKey, row.board_code);
                              applyOverrideLocalState(row.board_code, null);
                              if (selectedDocumentFolderKey) {
                                await loadBoards(projectKey, selectedDocumentFolderKey);
                              }
                              showScanMessage(`↩️ Vraćen automatski status: ${row.board_code}`);
                            } catch (err) {
                              console.error(err);
                              alert("Greška pri uklanjanju override-a.");
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 text-black dark:text-white"
                        >
                          Vrati automatski status
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Nema učitanih stavki za izabrani projekat.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Neprepoznati skenovi
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[950px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Kod
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Izvor
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Vreme
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Akcije
                </th>
              </tr>
            </thead>
            <tbody>
              {displayUnknownScans.map((scan, idx) => {
                const scanOverride = overrideMap[norm(scan.board_code)];

                return (
                  <tr
                    key={scan.id || `${scan.board_code}-${idx}`}
                    className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                  >
                    <td className="p-3 text-black dark:text-white font-medium">
                      {scan.board_code}
                    </td>
                    <td className="p-3">
                      <Badge tone="gray">{scan.source}</Badge>
                    </td>
                    <td className="p-3 text-black dark:text-white">
                      {scan.scanned_at ? new Date(scan.scanned_at).toLocaleString() : "-"}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const item = await saveProjectOverride(projectKey, {
                                board_code: scan.board_code,
                                forced_status: "scanned",
                                note: "Unknown scan manually accepted",
                              });

                              applyOverrideLocalState(scan.board_code, item);
                              if (selectedDocumentFolderKey) {
                                await loadBoards(projectKey, selectedDocumentFolderKey);
                              }
                              showScanMessage(`✅ Unknown scan ručno prihvaćen: ${scan.board_code}`);
                            } catch (err) {
                              console.error(err);
                              alert("Greška pri ručnom upisu statusa.");
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900"
                        >
                          Označi skenirano
                        </button>

                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const item = await saveProjectOverride(projectKey, {
                                board_code: scan.board_code,
                                forced_status: "not_scanned",
                                note: "Unknown scan manually marked not scanned",
                              });

                              applyOverrideLocalState(scan.board_code, item);
                              if (selectedDocumentFolderKey) {
                                await loadBoards(projectKey, selectedDocumentFolderKey);
                              }
                              showScanMessage(`⚠️ Unknown scan ručno označen kao nije skeniran: ${scan.board_code}`);
                            } catch (err) {
                              console.error(err);
                              alert("Greška pri ručnom upisu statusa.");
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                        >
                          Označi nije skenirano
                        </button>

                        {scanOverride && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await removeProjectOverride(projectKey, scan.board_code);
                                applyOverrideLocalState(scan.board_code, null);
                                if (selectedDocumentFolderKey) {
                                  await loadBoards(projectKey, selectedDocumentFolderKey);
                                }
                                showScanMessage(`↩️ Vraćen automatski status: ${scan.board_code}`);
                              } catch (err) {
                                console.error(err);
                                alert("Greška pri uklanjanju override-a.");
                              }
                            }}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 text-black dark:text-white"
                          >
                            Vrati automatski status
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {displayUnknownScans.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Nema neprepoznatih skenova.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-black dark:text-white">
            Aktivnosti
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead className="bg-gray-50 dark:bg-neutral-800">
              <tr className="border-b border-gray-200 dark:border-neutral-700">
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Vreme
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Akcija
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Stavka
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Korisnik
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Izvor
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Uređaj
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Lokacija
                </th>
                <th className="text-left p-3 text-gray-700 dark:text-gray-200 font-semibold">
                  Napomena
                </th>
              </tr>
            </thead>
            <tbody>
              {auditLog.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-100 dark:border-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-800/60 transition"
                >
                  <td className="p-3 text-black dark:text-white">
                    {new Date(item.created_at).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <Badge tone="gray">{formatAuditAction(item.action_type)}</Badge>
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {item.item_code || "-"}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {item.user_name || item.user_email || item.user_id || "-"}
                  </td>
                  <td className="p-3">
                    <Badge tone="blue">{item.source || "-"}</Badge>
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {item.device_type || "-"}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {item.latitude && item.longitude
                      ? `${item.latitude}, ${item.longitude}`
                      : "-"}
                  </td>
                  <td className="p-3 text-black dark:text-white">
                    {item.note || "-"}
                  </td>
                </tr>
              ))}

              {auditLog.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="p-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    Nema zabeleženih aktivnosti.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showClaimModal && claimBoard && (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
    <div className="w-full max-w-2xl rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-black dark:text-white">
            Reklamacija za tablu
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {claimBoard.board_code} — {claimBoard.material_model || "-"}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowClaimModal(false)}
          className="px-3 py-1 rounded-lg border border-gray-300 dark:border-neutral-700 text-black dark:text-white"
        >
          Zatvori
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm mb-1 text-black dark:text-white">
              Kod
            </label>
            <input
              value={claimBoard.board_code}
              disabled
              className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm mb-1 text-black dark:text-white">
              Tip problema
            </label>
            <select
              value={claimIssueType}
              onChange={(e) => setClaimIssueType(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
            >
              <option value="">-- Izaberi tip --</option>
              <option value="damage">Oštećenje</option>
              <option value="wrong_material">Pogrešan materijal</option>
              <option value="wrong_dimension">Pogrešna dimenzija</option>
              <option value="edge_issue">Problem sa kant trakom</option>
              <option value="machining_issue">Problem sa obradom</option>
              <option value="other">Ostalo</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-1 text-black dark:text-white">
            Opis reklamacije
          </label>
          <textarea
            value={claimDescription}
            onChange={(e) => setClaimDescription(e.target.value)}
            rows={5}
            placeholder="Unesi opis problema..."
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm mb-1 text-black dark:text-white">
            Slike
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              const toBase64 = (file) =>
                new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () =>
                    resolve({
                      name: file.name,
                      type: file.type,
                      size: file.size,
                      dataUrl: reader.result,
                    });
                  reader.onerror = reject;
                  reader.readAsDataURL(file);
                });

              const images = await Promise.all(files.map(toBase64));
              setClaimPhotos(images);
            }}
            className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
          />
        </div>

        {claimPhotos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {claimPhotos.map((img, idx) => (
              <div
                key={`${img.name}-${idx}`}
                className="rounded-xl overflow-hidden border border-gray-200 dark:border-neutral-700"
              >
                <img
                  src={img.dataUrl}
                  alt={img.name}
                  className="w-full h-28 object-cover"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-gray-200 dark:border-neutral-700 flex justify-end gap-3">
        <button
          type="button"
          onClick={() => setShowClaimModal(false)}
          className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-black dark:text-white"
        >
          Otkaži
        </button>

        <button
          type="button"
          onClick={async () => {
            try {
              const item = await createProjectClaim(projectKey, {
                board_code: claimBoard.board_code,
                issue_type: claimIssueType,
                description: claimDescription,
                photos: claimPhotos,
              });

              setClaims((prev) => [item, ...prev]);
              setShowClaimModal(false);
              showScanMessage(`🟠 Kreirana reklamacija za: ${claimBoard.board_code}`);
            } catch (err) {
              console.error(err);
              alert("Greška pri kreiranju reklamacije.");
            }
          }}
          className="px-4 py-2 rounded-xl bg-orange-500 text-white font-medium"
        >
          Sačuvaj reklamaciju
        </button>
      </div>
    </div>
  </div>
)}

      {showScanner && (
        <QrScanner
          isCodeInSystem={isCodeExpectedForProject}
          closeOnSuccess={false}
          allowContinuousScan={true}
          onScanSuccess={async (scannedCode, meta) => {
            await registerScan(scannedCode, "scanner", meta);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}